import { Injectable } from '@angular/core';
import API, { graphqlOperation } from '@aws-amplify/api';

import { annualAccountingReport } from '../../graphql/queries';
import {
  AnnualAccountingReportFilter,
  AnnualAccountingReportQuery,
  AnnualAccountingReportQueryVariables,
  AuctionType,
  BankAccountType
} from '../API.service';

export interface AnnualAccountingReportRow {
  auctionId: string | null;
  auctionStartDate: string | null;
  auctionClosingDate: string | null;
  title: string | null;
  auctionType: AuctionType | null;
  bankAccountType: BankAccountType | null;
  lotCount: number | null;
  soldLotCount: number | null;
  grossSales: number | null;
  accountsReceivableOutstanding: number | null;
  sellingExpenses: number | null;
  auctionPayables: number | null;
  salesManagementCommission: number | null;
}

export interface AnnualAccountingReportTotals {
  lotCount: number | null;
  soldLotCount: number | null;
  grossSales: number | null;
  accountsReceivableOutstanding: number | null;
  sellingExpenses: number | null;
  auctionPayables: number | null;
  salesManagementCommission: number | null;
}

export interface AnnualAccountingReportOmittedReason {
  reason: string | null;
  count: number | null;
}

export interface AnnualAccountingReport {
  rows: AnnualAccountingReportRow[];
  totals: AnnualAccountingReportTotals;
  omittedAuctionCount: number;
  omissionReasons: AnnualAccountingReportOmittedReason[];
}

@Injectable()
export class AnnualAccountingReportModelService {
  async loadReport(filter: AnnualAccountingReportFilter): Promise<AnnualAccountingReport> {
    const variables: AnnualAccountingReportQueryVariables = { filter };
    const response = await API.graphql(graphqlOperation(annualAccountingReport, variables)) as {
      data: AnnualAccountingReportQuery;
    };
    const report = response.data.annualAccountingReport;

    if (report == undefined) {
      throw new Error('Annual accounting report was not returned.');
    }

    return {
      rows: (report.rows || []).filter((row): row is any => row != undefined),
      totals: report.totals || {
        lotCount: 0,
        soldLotCount: 0,
        grossSales: 0,
        accountsReceivableOutstanding: 0,
        sellingExpenses: 0,
        auctionPayables: 0,
        salesManagementCommission: 0
      },
      omittedAuctionCount: report.omittedAuctionCount || 0,
      omissionReasons: (report.omissionReasons || []).filter((reason): reason is any => reason != undefined)
    };
  }
}
